// src/pages/Profile/components/PhotoProfileModal.jsx

import React from "react";
import {
  FaCamera,
  FaCloudUploadAlt,
  FaImage,
  FaLink,
  FaSave,
  FaTimes,
} from "react-icons/fa";

import {
  toAbsoluteUserPhotoUrl,
} from "../../../services/userPhotoServices";

import ActionButton from "../ui/ActionButton";
import { DEFAULT_AVATAR } from "../data/profileData";
import { cn, withBust } from "../utils/profileUtils";

export default function PhotoProfileModal({
  user,
  uploadMode,
  setUploadMode,
  fileInputRef,
  openFilePicker,
  handleFileChange,
  handlePhotoSave,
  handlePhotoUrlChange,
  isUploading,
  uploadProgress,
  outputFormat,
  setOutputFormat,
  imgVersion,
  imgOk,
  setImgOk,
  setIsCropOpen,
  onClose,
}) {
  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/75 px-4 py-6 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-[2rem] border border-white/10 bg-white p-6 shadow-2xl dark:bg-slate-950 sm:p-8"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:text-primary dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
          aria-label="Fermer"
        >
          <FaTimes />
        </button>

        <div className="pr-10">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-100 text-primary dark:bg-orange-500/10">
            <FaCamera />
          </div>

          <h3 className="mt-5 font-barlow text-3xl font-bold text-slate-950 dark:text-white">
            Photo de profil
          </h3>

          <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
            Ajoutez une photo par URL ou importez une image depuis votre
            ordinateur.
          </p>
        </div>

        <form onSubmit={handlePhotoSave} className="mt-7 space-y-6">
          <div className="grid gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => setUploadMode("url")}
              className={cn(
                "flex items-center justify-center gap-2 rounded-2xl border px-4 py-3 text-sm font-bold transition",
                uploadMode === "url"
                  ? "border-primary bg-primary text-white"
                  : "border-slate-200 bg-slate-50 text-slate-700 hover:border-primary/30 dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
              )}
            >
              <FaLink />
              Lien URL
            </button>

            <button
              type="button"
              onClick={() => setUploadMode("upload")}
              className={cn(
                "flex items-center justify-center gap-2 rounded-2xl border px-4 py-3 text-sm font-bold transition",
                uploadMode === "upload"
                  ? "border-primary bg-primary text-white"
                  : "border-slate-200 bg-slate-50 text-slate-700 hover:border-primary/30 dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
              )}
            >
              <FaCloudUploadAlt />
              Uploader
            </button>
          </div>

          {uploadMode === "url" ? (
            <div>
              <label className="mb-2 block text-sm font-bold text-slate-700 dark:text-slate-200">
                URL de la photo
              </label>

              <input
                type="text"
                value={user.photoURL || ""}
                onChange={(event) => handlePhotoUrlChange(event.target.value)}
                placeholder="https://… ou /uploads/…"
                className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10 dark:border-white/10 dark:bg-slate-950/70 dark:text-white"
              />

              <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
                Utilisez une URL absolue ou un chemin interne commençant par
                /uploads/.
              </p>
            </div>
          ) : (
            <div className="rounded-[1.7rem] border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-white/5">
              <input
                ref={fileInputRef}
                id="user-photo-file"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />

              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <ActionButton
                  icon={FaCloudUploadAlt}
                  onClick={openFilePicker}
                  variant="secondary"
                  disabled={isUploading}
                >
                  Choisir un fichier
                </ActionButton>

                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-slate-600 dark:text-slate-300">
                    Format
                  </span>

                  <select
                    className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 outline-none focus:border-primary dark:border-white/10 dark:bg-slate-950 dark:text-white"
                    value={outputFormat}
                    onChange={(event) => setOutputFormat(event.target.value)}
                    disabled={isUploading}
                  >
                    <option value="webp">WEBP</option>
                    <option value="jpg">JPG</option>
                    <option value="png">PNG</option>
                  </select>
                </div>
              </div>

              {isUploading && (
                <div className="mt-5">
                  <div className="h-3 overflow-hidden rounded-full bg-slate-200 dark:bg-white/10">
                    <div
                      className="h-full rounded-full bg-primary transition-all"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>

                  <p className="mt-2 text-xs font-bold text-slate-500 dark:text-slate-400">
                    {uploadProgress}%
                  </p>
                </div>
              )}

              <p className="mt-4 text-xs leading-5 text-slate-500 dark:text-slate-400">
                L’image sera traitée côté serveur en carré 400×400 et optimisée.
              </p>
            </div>
          )}

          <div className="rounded-[1.7rem] border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-white/5">
            <div className="flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-200">
              <FaImage className="text-primary" />
              Aperçu
            </div>

            <div className="mt-4 flex flex-col items-center gap-4 sm:flex-row">
              <div className="h-32 w-32 overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm dark:border-white/10">
                <img
                  key={imgVersion}
                  src={
                    user.photoURL
                      ? withBust(
                          toAbsoluteUserPhotoUrl(user.photoURL),
                          imgVersion
                        )
                      : withBust(DEFAULT_AVATAR, imgVersion)
                  }
                  alt="Aperçu"
                  onError={() => setImgOk(false)}
                  className="h-full w-full object-cover"
                />
              </div>

              <div className="flex flex-col gap-3">
                {user.photoURL && (
                  <ActionButton
                    icon={FaCamera}
                    onClick={() => setIsCropOpen(true)}
                    variant="secondary"
                    disabled={isUploading}
                  >
                    Recadrer / Zoom
                  </ActionButton>
                )}

                {!imgOk && (
                  <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-xs font-bold text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
                    Image impossible à charger.
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:justify-end">
            <ActionButton icon={FaTimes} onClick={onClose} variant="secondary">
              Fermer
            </ActionButton>

            <ActionButton icon={FaSave} type="submit">
              Sauvegarder
            </ActionButton>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs leading-5 text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-400">
            Il faudra peut-être vous déconnecter et reconnecter pour actualiser
            votre photo partout.
          </div>
        </form>
      </div>
    </div>
  );
}